import React, { useMemo, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import EmptyState from '../../shared/components/EmptyState';
import SlideOver from '../../shared/components/SlideOver';
import ConfirmDialog from '../../shared/components/ConfirmDialog';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const PRIORITY_CLASS = { low: 'status-green', medium: 'status-yellow', high: 'status-orange', critical: 'status-red' };
const PRIORITY_LABEL = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
const DIFFICULTY = [1, 2, 3, 4, 5];

const EMPTY_FORM = {
  fault_code: '', name: '', description: '', category: '', equipment_type: '',
  symptoms: '', troubleshooting_steps: '', common_causes: '', required_parts: '',
  estimated_time_minutes: '', difficulty_level: '3', priority: 'medium',
  safety_hazard: false, safety_notes: '',
};

function formToPayload(form) {
  let symptoms = [];
  let requiredParts = [];
  if (form.symptoms.trim()) {
    const parsed = JSON.parse(form.symptoms.trim());
    symptoms = Array.isArray(parsed) ? parsed : [parsed];
  }
  if (form.required_parts.trim()) {
    const parsed = JSON.parse(form.required_parts.trim());
    requiredParts = Array.isArray(parsed) ? parsed : [parsed];
  }
  return {
    fault_code: form.fault_code.trim(),
    name: form.name.trim(),
    description: form.description.trim(),
    category: form.category.trim(),
    equipment_type: form.equipment_type.trim(),
    symptoms,
    troubleshooting_steps: form.troubleshooting_steps.trim(),
    common_causes: form.common_causes.trim(),
    required_parts: requiredParts,
    estimated_time_minutes: form.estimated_time_minutes ? Number(form.estimated_time_minutes) : null,
    difficulty_level: form.difficulty_level ? Number(form.difficulty_level) : null,
    priority: form.priority,
    safety_hazard: form.safety_hazard,
    safety_notes: form.safety_notes.trim(),
  };
}

// Admin CRUD for faults_db (update_tasks.md §7.6). Safety fields are
// validated in the form here; hook enforcement is gated on TBC-9 (Phase 2).
export default function Faults() {
  const { data, add, update, remove } = useData();
  const { notify } = useToast();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [priority, setPriority] = useState('all');
  const [editing, setEditing] = useState(null); // fault id or 'new'
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const faults = useMemo(() => data.faults.filter((fault) => {
    if (category !== 'all' && fault.category !== category) return false;
    if (priority !== 'all' && fault.priority !== priority) return false;
    if (!query) return true;
    const haystack = `${fault.fault_code} ${fault.name} ${fault.equipment_type} ${fault.category} ${fault.description}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  }), [data.faults, query, category, priority]);

  const categories = useMemo(() => Array.from(new Set((data.faults || []).map((fault) => fault.category).filter(Boolean))), [data.faults]);

  const openNew = () => { setForm(EMPTY_FORM); setEditing('new'); };

  const openEdit = (fault) => {
    setForm({
      fault_code: fault.fault_code, name: fault.name, description: fault.description,
      category: fault.category, equipment_type: fault.equipment_type,
      symptoms: fault.symptoms && fault.symptoms.length ? JSON.stringify(fault.symptoms, null, 2) : '',
      troubleshooting_steps: fault.troubleshooting_steps, common_causes: fault.common_causes,
      required_parts: fault.required_parts && fault.required_parts.length ? JSON.stringify(fault.required_parts, null, 2) : '',
      estimated_time_minutes: fault.estimated_time_minutes ? String(fault.estimated_time_minutes) : '',
      difficulty_level: fault.difficulty_level ? String(fault.difficulty_level) : '3',
      priority: fault.priority || 'medium',
      safety_hazard: fault.safety_hazard, safety_notes: fault.safety_notes,
    });
    setEditing(fault.id);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.fault_code.trim()) { notify('Fault code is required', 'error'); return; }
    if (!form.name.trim()) { notify('Fault name is required', 'error'); return; }
    if (form.safety_hazard && !form.safety_notes.trim()) { notify('Safety notes are required for a safety hazard', 'error'); return; }
    let payload;
    try {
      payload = formToPayload(form);
    } catch {
      notify('Symptoms and required parts must be valid JSON arrays', 'error');
      return;
    }
    setBusy(true);
    try {
      if (editing === 'new') {
        await add('faults', payload);
        notify('Fault created');
      } else {
        await update('faults', editing, payload);
        notify('Fault saved');
      }
      setEditing(null);
    } catch (err) {
      notify(err.message || 'Could not save the fault', 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await remove('faults', deleting.id);
      notify('Fault deleted');
    } catch (err) {
      notify(err.message || 'Could not delete the fault', 'error');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Engineer</span>
          <h1>Faults Database</h1>
          <p className="field-hint">
            {faults.length} fault{faults.length === 1 ? '' : 's'} — engineers search these on the job.
          </p>
        </div>
        <div className="header-actions">
          <button className="primary-button" onClick={openNew}><SafeIcon icon={Icons.Plus} /> New fault</button>
        </div>
      </header>

      <div className="toolbar">
        <label className="search-input">
          <SafeIcon icon={Icons.Search} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search code, name or equipment…" />
        </label>
        {categories.length > 0 && (
          <select value={category} onChange={(event) => setCategory(event.target.value)} className="engineer-filter" aria-label="Filter by category">
            <option value="all">All categories</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        )}
        <select value={priority} onChange={(event) => setPriority(event.target.value)} className="engineer-filter" aria-label="Filter by priority">
          <option value="all">All priorities</option>
          {PRIORITIES.map((item) => <option key={item} value={item}>{PRIORITY_LABEL[item]}</option>)}
        </select>
      </div>

      {!data.faults.length ? (
        <EmptyState title="No faults yet" message="Add common faults so engineers have troubleshooting steps on the job." />
      ) : !faults.length ? (
        <EmptyState title="No matching faults" message="Try different filters or search." />
      ) : (
        <div className="faults-grid">
          {faults.map((fault) => (
            <article className="fault-card" key={fault.id}>
              <div className="card-top">
                <span className="eyebrow">{fault.fault_code}</span>
                <span className={`status-pill ${PRIORITY_CLASS[fault.priority] || 'status-gray'}`}>{PRIORITY_LABEL[fault.priority] || fault.priority}</span>
              </div>
              <h3>{fault.name}</h3>
              <p className="field-hint">{fault.equipment_type}{fault.category ? ` · ${fault.category}` : ''}</p>
              {fault.safety_hazard && <p className="field-hint" style={{ color: '#b91c1c', fontWeight: 600 }}>⚠ Safety hazard</p>}
              {fault.estimated_time_minutes ? <p className="field-hint">~{fault.estimated_time_minutes} min</p> : null}
              <div className="form-actions">
                <button className="link-button" onClick={() => openEdit(fault)}>Edit</button>
                <button className="link-button" onClick={() => setDeleting(fault)}>Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}

      <SlideOver open={editing !== null} title={editing === 'new' ? 'New fault' : 'Edit fault'} onClose={() => setEditing(null)}>
        <form className="drawer-form" onSubmit={save}>
          <div className="form-row">
            <label>Fault code<input value={form.fault_code} onChange={(event) => setForm((f) => ({ ...f, fault_code: event.target.value }))} placeholder="e.g. F-2301" required /></label>
            <label>Name<input value={form.name} onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))} placeholder="e.g. No hot water" required /></label>
          </div>
          <label>Description<textarea rows="2" value={form.description} onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))} /></label>
          <div className="form-row">
            <label>Category<input value={form.category} onChange={(event) => setForm((f) => ({ ...f, category: event.target.value }))} placeholder="e.g. Boilers" /></label>
            <label>Equipment type<input value={form.equipment_type} onChange={(event) => setForm((f) => ({ ...f, equipment_type: event.target.value }))} placeholder="e.g. Combi boiler" /></label>
          </div>
          <label>Symptoms (JSON array)
            <textarea rows="2" value={form.symptoms} onChange={(event) => setForm((f) => ({ ...f, symptoms: event.target.value }))} placeholder='["No hot water", "Pilot light off"]' />
          </label>
          <label>Troubleshooting steps<textarea rows="4" value={form.troubleshooting_steps} onChange={(event) => setForm((f) => ({ ...f, troubleshooting_steps: event.target.value }))} placeholder={"1. Check the condensate trap\n2. Reset the boiler\n3. …"} /></label>
          <label>Common causes<textarea rows="2" value={form.common_causes} onChange={(event) => setForm((f) => ({ ...f, common_causes: event.target.value }))} /></label>
          <label>Required parts (JSON array)
            <textarea rows="2" value={form.required_parts} onChange={(event) => setForm((f) => ({ ...f, required_parts: event.target.value }))} placeholder='["PCB 7716051388", "Thermistor"]' />
          </label>
          <div className="form-row">
            <label>Estimated minutes<input type="number" min="0" value={form.estimated_time_minutes} onChange={(event) => setForm((f) => ({ ...f, estimated_time_minutes: event.target.value }))} /></label>
            <label>Difficulty
              <select value={form.difficulty_level} onChange={(event) => setForm((f) => ({ ...f, difficulty_level: event.target.value }))}>
                {DIFFICULTY.map((level) => <option key={level} value={String(level)}>{level} — {level === 1 ? 'Basic' : level === 5 ? 'Advanced' : ''}</option>)}
              </select>
            </label>
          </div>
          <label>Priority
            <select value={form.priority} onChange={(event) => setForm((f) => ({ ...f, priority: event.target.value }))}>
              {PRIORITIES.map((item) => <option key={item} value={item}>{PRIORITY_LABEL[item]}</option>)}
            </select>
          </label>
          <label className="check-row"><input type="checkbox" checked={form.safety_hazard} onChange={(event) => setForm((f) => ({ ...f, safety_hazard: event.target.checked }))} /> Safety hazard</label>
          {form.safety_hazard && (
            <label>Safety notes
              <textarea rows="3" value={form.safety_notes} onChange={(event) => setForm((f) => ({ ...f, safety_notes: event.target.value }))} placeholder="Isolate gas supply before work — required when a hazard is flagged." required />
            </label>
          )}
          <div className="drawer-actions">
            <button type="button" className="secondary-button" onClick={() => setEditing(null)}>Cancel</button>
            <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : editing === 'new' ? 'Create fault' : 'Save changes'}</button>
          </div>
        </form>
      </SlideOver>

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete fault?"
        message={deleting ? `"${deleting.name}" (${deleting.fault_code}) will be removed.` : ''}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
